import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useRepoParams } from '../../hooks/useRepoParams';
import { get } from '../../api/client';
import { refToSegment } from '../../utils/refSegment';
import { RepoCodeLayout } from '../../components/repo/RepoCodeLayout';
import './FileContent.css';

/** 会话内 blob 缓存：同一文件重复打开不再请求 */
const blobCache = new Map<string, FileData>();

interface FileData {
    name: string;
    path: string;
    sha: string;
    size: number;
    type: string;
    encoding: string;
    content: string;
    isBinary: boolean;
    mimeType: string;
}

export function FileContentPage(props: { repoPath?: string; gitRef?: string; filePath?: string }) {
    const params = useParams();
    const { owner, repo } = useRepoParams();

    // Use props if available, otherwise fallback to params
    const repoPath = props.repoPath || (owner && repo ? `${owner}/${repo}` : '');
    const ref = props.gitRef || params['*']?.split('/')[0] || 'main'; // Note: if props.gitRef is passed, use it.
    // If props.filePath is passed, use it. Else parse from * params.
    const filePath = props.filePath !== undefined ? props.filePath : (params['*']?.split('/').slice(1).join('/') || '');

    // Note: useRepoParams might return empty if matched by *.
    // But we need repo name for breadcrumbs?
    // breadcrumbs uses `repo` variable.
    // If repoPath is "a/b/c", repo name implies "c" or full "a/b/c"?
    // In Breadcrumbs code: `repo` variable is used.


    const [file, setFile] = useState<FileData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!repoPath || !filePath) return;
        const cacheKey = `${repoPath}:${ref}:${filePath}`;
        const cached = blobCache.get(cacheKey);
        if (cached) {
            setFile(cached);
            setLoading(false);
            setError(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        get<FileData>(`/repos/${repoPath}/blob/${encodeURIComponent(ref)}/${filePath}`)
            .then((data) => {
                if (!cancelled) {
                    blobCache.set(cacheKey, data);
                    setFile(data);
                }
            })
            .catch((err: any) => {
                if (!cancelled) setError(err.message || '加载文件失败');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [repoPath, ref, filePath]);

    const getLanguage = (filename: string): string => {
        const ext = filename.split('.').pop()?.toLowerCase() || '';
        const languageMap: Record<string, string> = {
            ts: 'typescript',
            tsx: 'typescript',
            js: 'javascript',
            jsx: 'javascript',
            py: 'python',
            rb: 'ruby',
            go: 'go',
            rs: 'rust',
            java: 'java',
            c: 'c',
            cpp: 'cpp',
            h: 'c',
            hpp: 'cpp',
            css: 'css',
            scss: 'scss',
            less: 'less',
            html: 'html',
            json: 'json',
            yaml: 'yaml',
            yml: 'yaml',
            md: 'markdown',
            sql: 'sql',
            sh: 'bash',
            bash: 'bash',
            zsh: 'bash',
            toml: 'toml',
        };
        return languageMap[ext] || 'text';
    };

    const breadcrumbs = filePath.split('/').filter(Boolean);
    const fileName = breadcrumbs[breadcrumbs.length - 1] || '';
    const treePath = filePath.split('/').slice(0, -1).join('/');

    return (
        <RepoCodeLayout
            repoPath={repoPath}
            refName={ref}
            treePath={treePath}
            filePath={filePath}
        >
        <div className="file-content-page">
            {/* 工具栏 */}
            <div className="file-toolbar">
                <div className="breadcrumbs">
                    <Link to={`/${repoPath}/-/tree/${refToSegment(ref)}`} className="breadcrumb-link">
                        {repo}
                    </Link>
                    {breadcrumbs.slice(0, -1).map((part, index) => (
                        <span key={index}>
                            <span className="separator">/</span>
                            <Link
                                to={`/${repoPath}/-/tree/${refToSegment(ref)}/${breadcrumbs.slice(0, index + 1).join('/')}`}
                                className="breadcrumb-link"
                            >
                                {part}
                            </Link>
                        </span>
                    ))}
                    <span className="separator">/</span>
                    <span className="current-file">{fileName}</span>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {loading ? (
                <div className="loading">加载中...</div>
            ) : !file ? (
                <div className="error-state">文件不存在</div>
            ) : (
                <div className="file-viewer">
                    {/* 文件信息 */}
                    <div className="file-header">
                        <div className="file-info">
                            <span className="file-lines">
                                {file.isBinary ? '' : `${file.content.split('\n').length} 行`}
                            </span>
                            <span className="file-size">{formatFileSize(file.size)}</span>
                        </div>
                        <div className="file-actions">
                            <button className="action-btn" title="复制">
                                <svg viewBox="0 0 16 16" width="16" height="16">
                                    <path
                                        fill="currentColor"
                                        d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 0 1 0 1.5h-1.5a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-1.5a.75.75 0 0 1 1.5 0v1.5A1.75 1.75 0 0 1 9.25 16h-7.5A1.75 1.75 0 0 1 0 14.25ZM5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0 1 14.25 11h-7.5A1.75 1.75 0 0 1 5 9.25Z"
                                    />
                                </svg>
                            </button>
                            <a
                                href={`data:${file.mimeType};base64,${file.isBinary ? file.content : btoa(unescape(encodeURIComponent(file.content)))}`}
                                download={fileName}
                                className="action-btn"
                                title="下载"
                            >
                                <svg viewBox="0 0 16 16" width="16" height="16">
                                    <path
                                        fill="currentColor"
                                        d="M2.75 14A1.75 1.75 0 0 1 1 12.25v-2.5a.75.75 0 0 1 1.5 0v2.5c0 .138.112.25.25.25h10.5a.25.25 0 0 0 .25-.25v-2.5a.75.75 0 0 1 1.5 0v2.5A1.75 1.75 0 0 1 13.25 14Zm-1.143-6.573 4.644 4.644a.75.75 0 0 0 1.062 0l4.644-4.644a.75.75 0 0 0-1.062-1.06L8.5 8.814V1.75a.75.75 0 0 0-1.5 0v7.066l-2.895-2.895a.75.75 0 0 0-1.062 1.061l-.001.001.065.064Z"
                                    />
                                </svg>
                            </a>
                        </div>
                    </div>

                    {/* 文件内容 */}
                    <div className="file-content">
                        {file.isBinary ? (
                            <div className="binary-preview">
                                {file.mimeType.startsWith('image/') ? (
                                    <img
                                        src={`data:${file.mimeType};base64,${file.content}`}
                                        alt={fileName}
                                        className="image-preview"
                                    />
                                ) : (
                                    <div className="binary-info">
                                        <p>这是一个二进制文件 ({file.mimeType})</p>
                                        <p>大小: {formatFileSize(file.size)}</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <pre className="code-content">
                                <code className={`language-${getLanguage(fileName)}`}>
                                    {file.content.split('\n').map((line, index) => (
                                        <div key={index} className="code-line">
                                            <span className="line-number">{index + 1}</span>
                                            <span className="line-content">{line}</span>
                                        </div>
                                    ))}
                                </code>
                            </pre>
                        )}
                    </div>
                </div>
            )}
        </div>
        </RepoCodeLayout>
    );
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default FileContentPage;
